// It lives in its own module rather than inline in the panel so that step has one place to read
// from — the same reason `fertPreset`'s catalog is separate from `BuildRxDropdown`.

/* * Which Rx a product spends. `lime` reads the lime Rx, `p2o5` the phosphorus Rx, `k2o` the potassium. */
export type SpreadNutrient = "lime" | "p2o5" | "k2o";

export interface SpreadPreset {
  id: string;
  label: string;
  /*
   *
   * ABSENT on `Custom`, deliberately: a custom product can carry SEVERAL nutrients and the user
   * picks them, so a single value here would be a lie a reader could act on. Every consumer
   * therefore has to handle the multi-nutrient case rather than defaulting.
   */
  nutrient?: SpreadNutrient;
  efficiencyPct: number;
  /* * the (i) blurb on the card */
  desc: string;
  /* * the escape hatch, rendered last and styled apart — same convention as the Build Rx catalog */
  custom?: boolean;
}

/* * What the Rx is measured in before it becomes product — used by the card copy. */
export const PURE_UNIT: Record<SpreadNutrient, string> = {
  lime: "90% CCE lime",
  p2o5: "pure P₂O₅",
  k2o: "pure K₂O",
};

const NUTRIENT_WORD: Record<SpreadNutrient, string> = {
  lime: "lime",
  p2o5: "phosphorus",
  k2o: "potassium",
};

/*
 *
 * The description every product card carries. Written once rather than per preset because all three
 * claims are the same claim in each case, and three hand-written variants would drift: what Rx it
 * spends, and that Customize is where you change that. P and K divide by analysis. Lime multiplies.
 */
function describe(
  label: string,
  nutrient: SpreadNutrient,
  pct: number,
): string {
  if (nutrient === "lime") {
    // efficiencyPct stays the stored CCE percent. Multiplier 1.00 is analysis 90.
    return (
      "Spreads this field’s most recent lime Rx. " +
      "The Rx is tons of 90% CCE lime. " +
      "Multiplier 1.00 (the recs default) spreads that rate. " +
      "Use more for weaker lime (e.g. 1.50 ≈ 60% CCE), less for stronger.\n" +
      "Customize to spend a different Rx or change the multiplier."
    );
  }
  return (
    `Spreads this field’s most recent ${NUTRIENT_WORD[nutrient]} Rx as ${label}. ` +
    `The Rx is ${PURE_UNIT[nutrient]}, so the product rate is that rate ÷ ${pct}% — the ` +
    `analysis assumed here.\n` +
    `Customize to spend a different Rx or change the analysis.`
  );
}

export const SPREAD_PRESETS: SpreadPreset[] = [
  {
    id: "spread-lime",
    label: "Lime",
    nutrient: "lime",
    efficiencyPct: 90,
    desc: describe("lime", "lime", 90),
  },
  {
    id: "spread-map",
    label: "MAP",
    nutrient: "p2o5",
    efficiencyPct: 52,
    desc: describe("MAP", "p2o5", 52),
  },
  {
    id: "spread-mop",
    label: "Muriate of Potash",
    nutrient: "k2o",
    efficiencyPct: 60,
    desc: describe("Muriate of Potash", "k2o", 60),
  },
  {
    id: "spread-dap",
    label: "DAP",
    nutrient: "p2o5",
    efficiencyPct: 46,
    desc: describe("DAP", "p2o5", 46),
  },
  {
    id: "spread-sop",
    label: "Sulfate of Potash",
    nutrient: "k2o",
    efficiencyPct: 50,
    desc: describe("Sulfate of Potash", "k2o", 50),
  },
  {
    id: "spread-custom",
    label: "Custom",
    efficiencyPct: 100,
    desc: "Pick the nutrients, the Rx for each, and your product\u2019s analysis for each.\nStart here for a blend, or for a product that is not one of the five.",
    custom: true,
  },
];

/* * The five real products, in card order. `Custom` is rendered apart, so it is excluded here. */
export const SPREAD_PRODUCTS = SPREAD_PRESETS.filter((p) => !p.custom);
